import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, ScrollView, Modal, Pressable, useWindowDimensions,
} from 'react-native';

export const C = {
  text: '#111827',
  accent: '#16A34A',
  danger: '#DC2626',
  amber: '#D97706',
  border: '#E5E7EB',
  bg: '#F7F8FA',
  muted: '#6B7280',
  teal: '#0D9488',
  tealDark: '#0F766E',
  indigo: '#6366F1',
};

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export function txnDate(ts: any): string {
  if (!ts) return '—';
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

export const MIcon = ({ name, size = 18, color = C.muted, style }: { name: string; size?: number; color?: string; style?: any }) => (
  <span className="material-icons-round" style={{ fontSize: size, color, lineHeight: 1, verticalAlign: 'middle', ...style }}>{name}</span>
);

// ── Right-side slide-in drawer ───────────────────────────────────
export function RightDrawer({
  visible, onClose, title, subtitle, children,
}: { visible: boolean; onClose: () => void; title: string; subtitle?: string; children: React.ReactNode }) {
  const { width } = useWindowDimensions();
  const w = Math.min(460, Math.max(320, width * 0.9));
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, flexDirection: 'row' }}>
        <Pressable style={dr.backdrop} onPress={onClose} />
        <View style={[dr.panel, { width: w }]}>
          <View style={dr.header}>
            <View style={{ flex: 1 }}>
              <Text style={dr.title} numberOfLines={1}>{title}</Text>
              {subtitle ? <Text style={dr.subtitle} numberOfLines={1}>{subtitle}</Text> : null}
            </View>
            <TouchableOpacity onPress={onClose} style={dr.closeBtn}>
              <MIcon name="close" size={20} color={C.text} />
            </TouchableOpacity>
          </View>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 60 }}>
            {children}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

// ── Filter chips ─────────────────────────────────────────────────
export function FilterChips({
  options, value, onChange,
}: { options: { label: string; value: number; count: number }[]; value: number; onChange: (v: number) => void }) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
      {options.map(o => {
        const selected = value === o.value;
        return (
          <TouchableOpacity
            key={o.value}
            onPress={() => onChange(o.value)}
            style={[chip.wrap, selected && chip.wrapActive]}
          >
            <Text style={[chip.txt, selected && chip.txtActive]}>{o.label} ({o.count})</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ── Pay / collect modal ──────────────────────────────────────────
export function PayModal({
  visible, onClose, title, entityName, remaining, full, methods, onConfirm,
}: {
  visible: boolean;
  onClose: () => void;
  title: string;
  entityName: string;
  remaining: number;
  full: boolean;                  // when true amount is locked to remaining
  methods: string[];
  onConfirm: (args: { amount: number; method: string; reference: string; notes: string }) => Promise<void>;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(methods[0] || 'Bank Transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setAmount(full ? String(Math.round(remaining)) : '');
      setMethod(methods[0] || 'Bank Transfer');
      setReference('');
      setNotes('');
      setError('');
      setSaving(false);
    }
  }, [visible, full, remaining, methods]);

  const submit = async () => {
    const amt = full ? remaining : Number(amount);
    if (!amt || isNaN(amt) || amt <= 0) { setError('Enter an amount greater than 0'); return; }
    if (amt > remaining + 0.5) { setError(`Amount can't exceed the remaining ${inr(remaining)}`); return; }
    if (reference.trim().length < 4) { setError('Reference / UTR must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await onConfirm({ amount: amt, method, reference: reference.trim(), notes: notes.trim() });
    } catch (e) {
      setError('Failed to record. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => !saving && onClose()}>
      <Pressable style={pm.overlay} onPress={() => !saving && onClose()}>
        <Pressable style={pm.card} onPress={() => {}}>
          <Text style={pm.title}>{title}</Text>
          <Text style={pm.subtitle}>{entityName} · Remaining {inr(remaining)}</Text>

          <Text style={pm.label}>Amount (₹)</Text>
          <TextInput
            style={[pm.input, full && pm.inputDisabled, error && pm.inputError]}
            value={full ? String(Math.round(remaining)) : amount}
            editable={!full}
            onChangeText={(v) => { setAmount(v.replace(/[^0-9]/g, '')); if (error) setError(''); }}
            placeholder="0"
            placeholderTextColor={C.muted}
            keyboardType="numeric"
          />

          <Text style={pm.label}>Payment Method</Text>
          <select value={method} onChange={(e: any) => setMethod(e.target.value)} style={selectStyle}>
            {methods.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <Text style={pm.label}>Reference / UTR ID</Text>
          <TextInput
            style={[pm.input, error && pm.inputError]}
            value={reference}
            onChangeText={(v) => { setReference(v); if (error) setError(''); }}
            placeholder="Min. 4 characters"
            placeholderTextColor={C.muted}
          />

          <Text style={pm.label}>Notes (optional)</Text>
          <TextInput
            style={[pm.input, { height: 60 }]}
            value={notes}
            onChangeText={setNotes}
            placeholder="Any remarks…"
            placeholderTextColor={C.muted}
            multiline
          />

          {error ? <Text style={pm.errorTxt}>{error}</Text> : null}

          <View style={pm.actions}>
            <TouchableOpacity style={pm.cancelBtn} onPress={onClose} disabled={saving}>
              <Text style={pm.cancelTxt}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[pm.confirmBtn, saving && { opacity: 0.6 }]} onPress={submit} disabled={saving}>
              {saving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={pm.confirmTxt}>Confirm</Text>}
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Small reusable bits ──────────────────────────────────────────
export const StatusBadge = ({ status }: { status: 'PAID' | 'PARTIAL' | 'DUE' }) => {
  const color = status === 'PAID' ? C.accent : status === 'PARTIAL' ? C.amber : C.danger;
  return (
    <View style={[badge.wrap, { backgroundColor: color + '22' }]}>
      <Text style={[badge.txt, { color }]}>{status}</Text>
    </View>
  );
};

export function rowStatus(isPaid: boolean, paidSoFar: number): 'PAID' | 'PARTIAL' | 'DUE' {
  if (isPaid) return 'PAID';
  if (paidSoFar > 0) return 'PARTIAL';
  return 'DUE';
}

export const KV = ({ label, value, color = C.text, bold }: { label: string; value: string; color?: string; bold?: boolean }) => (
  <View style={kv.row}>
    <Text style={kv.label}>{label}</Text>
    <Text style={[kv.value, { color, fontWeight: bold ? '900' : '700' }]}>{value}</Text>
  </View>
);

export const selectStyle: React.CSSProperties = {
  width: '100%', height: 44, backgroundColor: '#fff', borderRadius: 10,
  border: `1.5px solid ${C.border}`, paddingLeft: 12, paddingRight: 12, fontSize: 14,
  color: C.text, cursor: 'pointer', outline: 'none', marginBottom: 4,
};

const dr = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)' },
  panel: { height: '100%', backgroundColor: C.bg, shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.2, shadowRadius: 20 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border },
  title: { fontSize: 16, fontWeight: '800', color: C.text },
  subtitle: { fontSize: 12, color: C.muted, marginTop: 2 },
  closeBtn: { padding: 6, borderRadius: 8 },
});

const chip = StyleSheet.create({
  wrap: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, backgroundColor: '#fff', borderWidth: 1, borderColor: C.border },
  wrapActive: { backgroundColor: C.teal, borderColor: C.teal },
  txt: { fontSize: 12, fontWeight: '800', color: C.muted },
  txtActive: { color: '#fff' },
});

const badge = StyleSheet.create({
  wrap: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  txt: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4 },
});

const kv = StyleSheet.create({
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 5 },
  label: { fontSize: 13, color: C.muted, fontWeight: '600', flex: 1 },
  value: { fontSize: 13, color: C.text },
});

const pm = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24 },
  title: { fontSize: 18, fontWeight: '800', color: C.text, marginBottom: 4 },
  subtitle: { fontSize: 13, color: C.muted, marginBottom: 8 },
  label: { fontSize: 12, fontWeight: '700', color: C.text, marginBottom: 6, marginTop: 10 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1.5, borderColor: C.border, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: C.text, outlineStyle: 'none' } as any,
  inputDisabled: { backgroundColor: C.bg, color: C.muted },
  inputError: { borderColor: C.danger },
  errorTxt: { fontSize: 12, color: C.danger, marginTop: 8 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.text, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: C.accent, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});

// Shared table/layout styles reused by the payroll screens.
export const sx = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.bg, padding: 16 },
  monthRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14 },
  monthBtn: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#fff', borderWidth: 1, borderColor: C.border, justifyContent: 'center', alignItems: 'center' },
  monthBtnDisabled: { backgroundColor: '#f1f5f9', borderColor: '#eef2f6' },
  monthBtnTxt: { fontSize: 22, fontWeight: '700', color: C.teal, lineHeight: 24 },
  monthBtnTxtDisabled: { color: '#cbd5e1' },
  monthLabelWrap: { minWidth: 180, alignItems: 'center' },
  monthLabel: { fontSize: 18, fontWeight: '800', color: C.text },

  summaryCard: { backgroundColor: C.teal, borderRadius: 16, padding: 20, marginBottom: 16, overflow: 'hidden' },
  summaryRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around' },
  summaryItem: { flex: 1, alignItems: 'center' },
  summaryDivider: { width: 1, height: 48, backgroundColor: 'rgba(255,255,255,0.2)' },
  summaryLabel: { fontSize: 12, fontWeight: '600', color: 'rgba(255,255,255,0.85)', marginBottom: 6, letterSpacing: 0.4 },
  summaryValueBig: { fontSize: 28, fontWeight: '900', color: '#fff' },
  summaryValue: { fontSize: 22, fontWeight: '800', color: '#fff' },
  summaryCaption: { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 4 },

  controls: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14 },
  tabRow: { flexDirection: 'row', gap: 8 },
  tab: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 20, backgroundColor: '#fff', borderWidth: 1, borderColor: C.border },
  tabActive: { backgroundColor: C.teal, borderColor: C.teal },
  tabTxt: { fontSize: 13, fontWeight: '700', color: C.muted },
  tabTxtActive: { color: '#fff' },
  refreshBtn: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#fff', borderWidth: 1, borderColor: C.border, justifyContent: 'center', alignItems: 'center' },
  searchBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingHorizontal: 14, height: 44 },
  searchInput: { flex: 1, height: 44, fontSize: 14, color: C.text, outlineStyle: 'none' } as any,

  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  loadingTxt: { marginTop: 12, color: C.muted },
  empty: { alignItems: 'center', paddingTop: 50 },
  emptyTxt: { fontSize: 15, color: C.muted, fontWeight: '600' },

  card: { backgroundColor: '#fff', borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 10 },
  rowName: { fontSize: 14, fontWeight: '700', color: C.text },
  rowSub: { fontSize: 10, color: C.muted, marginTop: 1 },
  tapPay: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 },
  tapPayTxt: { color: C.accent, fontSize: 12, fontWeight: '800' },

  drawerCard: { backgroundColor: '#fff', borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 16, marginBottom: 14 },
  payBtnRow: { flexDirection: 'row', gap: 10, marginBottom: 14 },
  partialBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, borderWidth: 1.5, borderColor: C.accent, alignItems: 'center' },
  partialTxt: { color: C.accent, fontWeight: '800', fontSize: 13 },
  fullBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: C.accent, alignItems: 'center' },
  fullTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },
  sectionLabel: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, marginBottom: 8 },
  orderCard: { backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: C.border, padding: 12, marginBottom: 8 },
  chipPill: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, flex: 1 },
});

export const ChipStat = ({ icon, label, value, color }: { icon: string; label: string; value: string; color: string }) => (
  <View style={[sx.chipPill, { backgroundColor: color + '16' }]}>
    <MIcon name={icon} size={15} color={color} />
    <View style={{ flex: 1 }}>
      <Text style={{ fontSize: 8, fontWeight: '700', color: C.muted, letterSpacing: 0.2 }}>{label.toUpperCase()}</Text>
      <Text style={{ fontSize: 13, fontWeight: '900', color }}>{value}</Text>
    </View>
  </View>
);
